import React from "react";
import { useSelector } from "react-redux";
import { userTypeSelector, authUidSelector } from "store/userSliceHelpers";
import { Navigate } from "react-router-dom";
import { useAuth } from "auth";
import SessionChangedScreen from "components/session-changed/SessionChangedScreen";
import {
  identityState,
  isAuthorized,
} from "components/protected-route/access";

interface Props {
  allowedUserTypes: string[];
  redirectTo: string;
  children: React.ReactNode;
}

// Guards the login form. Two jobs:
//
//  1. Keep an already-signed-in cardholder off it, so a second account cannot take
//     over this browser's shared credentials (the __session cookie and Firebase's
//     IndexedDB user are origin-scoped, so a second sign-in replaces them for
//     every tab).
//  2. Refuse to serve it to a tab whose identity no longer matches the browser's
//     Firebase user — the case where another tab has already signed someone else
//     in. Such a tab gets NEITHER the login form NOR the dashboard — only a
//     sign-out, the one exit that cannot hand this browser to a second
//     principal (CRYP-4925).
function GuestOnlyRoute({ allowedUserTypes, redirectTo, children }: Props) {
  const { user, initializing } = useAuth();
  const userType = useSelector(userTypeSelector);
  const authUid = useSelector(authUidSelector);

  // Wait for Firebase to restore the persisted user before deciding. `user` is
  // null while this runs, so rendering the form here would hand a working login
  // form to someone who is already signed in for as long as the restore takes.
  if (initializing) return null;

  const identity = identityState(user, authUid);

  if (identity === "mismatched") return <SessionChangedScreen />;

  // Only a fully bound and authorized visitor gets bounced to the dashboard. A
  // bound visitor without a userType yet is mid-sign-in — sign-in dispatches
  // authUid atomically with the auth state, then userType a moment later — so the
  // form must keep rendering for that flow to finish.
  if (identity === "bound" && isAuthorized(userType, allowedUserTypes)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export default GuestOnlyRoute;
